/**
 * The web build's motion language (components/motion): timings, distances and the small
 * decisions behind them. No React and no DOM here, so all of it is tested in Node.
 *
 * Every screen moves the same way because every animation reads from this one file.
 */

/** Fast start, long gentle settle: things arrive, they don't bounce. */
export const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

export const MOTION = {
  /** A section rising into place when a page opens or new content arrives. */
  enterDuration: 0.45,
  enterDistance: 14,
  /** Gap between sections in a cascade, and how many get their own beat - a long page
   *  never makes the reader wait for its last section. */
  staggerStep: 0.055,
  staggerMaxSteps: 7,
  /** Reduced motion (the OS setting), and small things like icons: a short fade, no travel. */
  fadeDuration: 0.2,
  /** Anything shorter than this fades in place - an icon rising 14px would leave its button. */
  enterMinTravelHeight: 40,
  /** Arrivals animated per batch; past this (a long list rendered at once) the rest just appear. */
  arrivalBatchLimit: 24,
  /** Elevated cards rise this many pixels under the mouse. */
  hoverLift: 3,
  /** Pressing shrinks small buttons by at most this much... */
  pressMaxShrink: 0.04,
  /** ...and moves a big card's edges this many pixels at most, so it never lurches. */
  pressMaxTravel: 6,
  /** Popup cards settle from slightly smaller and lower. */
  dialogScaleFrom: 0.96,
  dialogDistance: 10,
  dialogDuration: 0.34,
  /** Dashboard numbers counting up to their value. */
  countUpDuration: 0.9,
  /** Sidebar width, so the mobile drawer slides exactly its own width. */
  drawerWidth: 260,
  /** Snappy, no wobble: presses and the sidebar's active pill. */
  spring: { type: 'spring', stiffness: 520, damping: 34, mass: 0.7 },
  /** Softer: hover lifts, drawers and menus. */
  softSpring: { type: 'spring', stiffness: 300, damping: 30 },
} as const;

/** Seconds before the section at `index` of a cascade starts. */
export function cascadeDelay(index: number): number {
  const step = Math.min(Math.max(index, 0), MOTION.staggerMaxSteps);
  return Math.round(step * MOTION.staggerStep * 1000) / 1000;
}

/** How far an element of this height rises as it enters: small ones only fade. */
export function entranceDistanceFor(height: number): number {
  return height >= MOTION.enterMinTravelHeight ? MOTION.enterDistance : 0;
}

/**
 * How far a pressed element shrinks: a fixed feel for buttons, but a large card moves
 * only a few pixels (a flat 4% would pull a 1000px card in by 20px each side).
 */
export function pressScaleFor(width: number, height: number): number {
  const longest = Math.max(width, height, 1);
  const shrink = Math.min(MOTION.pressMaxShrink, (2 * MOTION.pressMaxTravel) / longest);
  return Math.round((1 - shrink) * 10000) / 10000;
}

export interface SurfaceMetrics {
  width: number;
  height: number;
  /** The computed box-shadow; react-native-web turns RN shadow styles into this. */
  boxShadow: string;
}

/**
 * Whether a pressable should lift under the mouse: only raised surfaces (cards, tiles)
 * that have a shadow to lift from. Icons, links and flat rows stay put, and so does
 * anything as big as a page section.
 */
export function isLiftableSurface({ width, height, boxShadow }: SurfaceMetrics): boolean {
  const hasShadow = !!boxShadow && boxShadow !== 'none';
  return hasShadow && width >= 100 && height >= 48 && height <= 480;
}

/**
 * The opacity an element should fade up to: what its own styles give it. Null when it
 * is meant to be invisible, so the entrance leaves it alone.
 */
export function entranceOpacity(computedOpacity: string | null | undefined): number | null {
  const parsed = parseFloat(computedOpacity ?? '');
  if (!Number.isFinite(parsed)) return 1;
  return parsed <= 0 ? null : parsed;
}

/** The two inline styles an animation writes. */
export interface InlineMotionStyle {
  opacity: string;
  transform: string;
}

/**
 * react-native-web writes dynamic styles inline, so an animation's leftovers must not
 * overwrite them. Take what was there before animating, and put exactly that back after.
 */
export function captureInline(node: { style: InlineMotionStyle }): InlineMotionStyle {
  return { opacity: node.style.opacity, transform: node.style.transform };
}

export function restoreInline(node: { style: InlineMotionStyle }, saved: InlineMotionStyle): void {
  node.style.opacity = saved.opacity;
  node.style.transform = saved.transform;
}

/**
 * An element's own inline styles across animations that overlap - a page left and come
 * back to mid-entrance. The first animation takes the snapshot; one that starts before
 * it is put back reuses it (what is inline by then is the animation's, not the element's);
 * and only the latest animation may put it back.
 */
export class InlineStyleLedger {
  private snapshots = new WeakMap<object, InlineMotionStyle>();
  private latest = new WeakMap<object, number>();

  /** Before animating `node`: returns the ticket that may later release it. */
  claim(node: { style: InlineMotionStyle }): number {
    if (!this.snapshots.has(node)) this.snapshots.set(node, captureInline(node));
    const ticket = (this.latest.get(node) ?? 0) + 1;
    this.latest.set(node, ticket);
    return ticket;
  }

  /** Puts the snapshot back on, keeping it, so the element's real styles can be read. */
  reapply(node: { style: InlineMotionStyle }): void {
    const snapshot = this.snapshots.get(node);
    if (snapshot) restoreInline(node, snapshot);
  }

  /** Puts the snapshot back for good - unless a newer animation has claimed the node. */
  release(node: { style: InlineMotionStyle }, ticket: number): boolean {
    if (this.latest.get(node) !== ticket) return false;
    this.reapply(node);
    this.snapshots.delete(node);
    return true;
  }
}

/**
 * A popup's card, as opposed to the full-screen dimmed backdrop around it: anything
 * clearly smaller than the window in either direction.
 */
export function isFloatingCard(
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): boolean {
  if (width <= 0 || height <= 0) return false;
  return width < viewportWidth * 0.96 || height < viewportHeight * 0.96;
}

/**
 * Whether a pressed element gets the shrink. Not a full-window one: that is a tap-to-close
 * backdrop, and shrinking it would show the page round its edges.
 */
export function pressFeedbackApplies(
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): boolean {
  return isFloatingCard(width, height, viewportWidth, viewportHeight);
}
